import type { ReactNode } from 'react';
import React from 'react';
import { styled } from 'baseui';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import type { AnnotationLabelT, RangeSpanT } from '../../../../../common/types/types';
import type { KeyFrameType } from '../../../types';
import { KEY_FRAME_TYPE, KEYFRAME_SIZE, TIMELINE_CONTAINER_GUTTER } from '../constants';
import { getAutomatedKeyFrameLeftPosition, getKeyFrameBorder, getKeyFrameColor } from './utils';
const Timeline = styled<'div', {
  $left: number;
  $top: number;
  $keyFrameSize: string;
}>('div', ({
  $left,
  $top,
  $keyFrameSize
}) => ({
  position: 'absolute',
  left: `${$left}px`,
  top: `${$top}px`,
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'auto',
  height: `${$keyFrameSize}px`,
  ':hover': {
    cursor: 'pointer'
  }
}));
const KeyFrameIcon = styled<'div', {
  $color: string;
  $left: number;
  $keyFrameSize: string;
  $accentTimeline?: boolean;
  $isAutomated?: boolean;
  $isLatestTracked?: boolean;
}>('div', ({
  $color,
  $left,
  $keyFrameSize,
  $accentTimeline,
  $isAutomated,
  $theme,
  $isLatestTracked
}) => ({
  height: $isAutomated ? `${parseFloat($keyFrameSize) * 0.6}px` : `${$keyFrameSize}px`,
  width: $isAutomated ? `${parseFloat($keyFrameSize) * 0.6}px` : `${$keyFrameSize}px`,
  backgroundColor: getKeyFrameColor($isAutomated, $isLatestTracked, $color, $theme),
  border: getKeyFrameBorder($isAutomated, $isLatestTracked, $theme),
  transform: 'rotate(45deg)',
  position: 'absolute',
  pointerEvents: 'auto',
  left: $isAutomated ? getAutomatedKeyFrameLeftPosition($left, $keyFrameSize) : `${$left}px`,
  borderRadius: $isAutomated ? '50%' : '0',
  ...($accentTimeline ? {
    cursor: 'pointer',
    border: `1px solid ${$theme.colors.borderSelected}`,
    transform: 'rotate(45deg) scale(1.2)'
  } : {})
}));
const Connector = styled<'div', {
  $color: string;
  $width: number;
  $accentTimeline?: boolean;
}>('div', ({
  $color,
  $width,
  $accentTimeline,
  $theme
}) => ({
  height: '2px',
  width: `${$width}px`,
  backgroundColor: $color,
  ...($accentTimeline ? {
    cursor: 'pointer',
    border: `1px solid ${$theme.colors.borderSelected}`
  } : {})
}));
type PropsT = {
  id: string;
  endFrame: number;
  startFrame: number;
  color: string;
  keyFrames: Array<[number, KeyFrameType]>;
  maxFrames: number;
  accentTimeline?: boolean;
  depth: number;
  annotationLabel: AnnotationLabelT;
  frameSize: number;
  currentFrame: number;
  timelineHeight?: number;
  timeLineContainerGutter?: number;
  startOffset?: number;
  Icon: (a: {
    strokeColor?: string;
  }) => ReactNode;
  onClick?: (e: Event) => void;
  onChange: (endFrame: Map<string, RangeSpanT>, close: () => void, isAnnotationTracking?: boolean) => Promise<void> | void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onDelete: (...args: any) => void;
  frames?: {
    annotationStore: {
      [annotationId: string]: unknown;
    };
  }[];
  actionConfig?: string[];
  trackedSequence?: {
    start: number;
    end: number;
    annotationId: string;
  } | null;
  setTrackedSequence?: (sequence: {
    start: number;
    end: number;
    annotationId: string;
  } | null) => void;
};
export const AnnotationTimelinePanel = ({
  id,
  startFrame,
  endFrame,
  keyFrames,
  depth,
  color,
  annotationLabel,
  accentTimeline,
  maxFrames,
  frameSize,
  timelineHeight = 0,
  timeLineContainerGutter = TIMELINE_CONTAINER_GUTTER,
  startOffset = 0,
  currentFrame,
  Icon,
  onClick,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onChange,
  frames,
  actionConfig,
  trackedSequence,
  setTrackedSequence
}: PropsT) => {
  return <StatefulPopover showArrow placement={PLACEMENT.right}
  // @ts-expect-error todo(ts-migration) TS2322 Type '(e: Event) => void' is not assignable to type '(e: SyntheticEvent<Element, Event>) => unknown'.
  onClick={onClick} content={() => <div>Deprecated</div>} overrides={{
    Body: {
      style: ({
        $theme: {
          colors
        }
      }) => ({
        width: '250px',
        padding: '20px',
        paddingBottom: '10px',
        backgroundColor: colors.primaryB,
        margin: '0 20px 10px 20px'
      })
    },
    Arrow: {
      style: ({
        $theme: {
          colors
        }
      }) => ({
        backgroundColor: colors.primaryB,
        boxShadow: 'none'
      })
    }
  }} autoFocus={false}>
      <Timeline onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid={`timeline_${id}`} data-selector-id={`timeline_${id}`} $top={depth * timelineHeight + timeLineContainerGutter} $left={frameSize * startFrame} $keyFrameSize={KEYFRAME_SIZE}>
        {endFrame - startFrame > 0 && <>
            <Connector data-testid={`timeline_${id}_connector`} $color={color} $width={(endFrame - startFrame + 1) * frameSize - startOffset} $accentTimeline={accentTimeline} />
          </>}
        {keyFrames.map(([frame, type]) => <KeyFrameIcon key={frame} data-testid={`timeline_${id}_keyframe_${frame}`} $color={color} $left={frameSize * (frame - startFrame)} $keyFrameSize={KEYFRAME_SIZE} $accentTimeline={accentTimeline} $isAutomated={type === KEY_FRAME_TYPE.AUTOMATED} $isLatestTracked={!!trackedSequence && trackedSequence.annotationId === id && trackedSequence.start <= frame && trackedSequence.end >= frame} />)}
      </Timeline>
    </StatefulPopover>;
};